import { useState } from "react";
import { KeyRound, Pencil, Trash2, UserPlus, Users } from "lucide-react";
import ConfirmDialog from "../components/ConfirmDialog";
import Loader from "../components/Loader";
import PasswordFormModal from "../components/PasswordFormModal";
import UserFormModal from "../components/UserFormModal";
import { useApplicationsContext } from "../context/ApplicationsContext";
import { useAuth } from "../context/AuthContext";
import useManagedApplications from "../hooks/useManagedApplications";
import useUsers from "../hooks/useUsers";

export default function ManageUsers() {
  const { users, loading, error, createUser, updateUser, setUserPassword, deleteUser } = useUsers();
  const { applications, loading: applicationsLoading } = useManagedApplications();
  const { user: currentUser } = useAuth();
  const { reload: reloadNavApplications } = useApplicationsContext();

  const [dialog, setDialog] = useState(null); // {type: 'create'|'edit'|'password'|'delete', user}
  const [busy, setBusy] = useState(false);
  const [dialogError, setDialogError] = useState("");

  const closeDialog = () => {
    setDialog(null);
    setDialogError("");
    setBusy(false);
  };

  const run = async (action) => {
    setBusy(true);
    setDialogError("");
    try {
      await action();
      closeDialog();
      // Permission changes can add or remove applications from your own sidebar.
      reloadNavApplications();
    } catch (e) {
      setDialogError(e.message);
      setBusy(false);
    }
  };

  const permissionBadges = (user) => {
    if (user.is_admin) {
      return [{ key: "admin", label: "مدیر سیستم", tone: "admin" }];
    }
    const badges = [];
    if (user.can_manage_users) badges.push({ key: "users", label: "مدیریت کاربران", tone: "info" });
    if (user.can_manage_applications)
      badges.push({ key: "apps", label: "مدیریت اپلیکیشن‌ها", tone: "info" });
    if (badges.length === 0) badges.push({ key: "none", label: "بدون دسترسی مدیریتی", tone: "muted" });
    return badges;
  };

  const applicationNames = (user) => {
    if (user.is_admin) {
      return "همه اپلیکیشن‌ها";
    }
    if (user.application_ids.length === 0) {
      return "—";
    }
    return applications
      .filter((application) => user.application_ids.includes(application.id))
      .map((application) => application.title)
      .join("، ");
  };

  return (
    <>
      <div className="page-head page-head-row">
        <div>
          <h1>مدیریت کاربران</h1>
          <p>کاربران سامانه و دسترسی‌های هرکدام را از اینجا مدیریت کنید.</p>
        </div>
        <button type="button" className="btn btn-primary" onClick={() => setDialog({ type: "create" })}>
          <UserPlus size={18} strokeWidth={1.9} aria-hidden="true" />
          کاربر جدید
        </button>
      </div>

      {(loading || applicationsLoading) && <Loader message="در حال دریافت کاربران…" />}

      {!loading && error && <div className="alert alert-error">{error}</div>}

      {!loading && !applicationsLoading && !error && (
        <div className="card table-card">
          <table className="table">
            <thead>
              <tr>
                <th>کاربر</th>
                <th>وضعیت</th>
                <th>دسترسی‌های مدیریتی</th>
                <th>اپلیکیشن‌ها</th>
                <th className="table-actions-head">عملیات</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && (
                <tr>
                  <td colSpan={5}>
                    <div className="state">
                      <div className="state-icon" aria-hidden="true">
                        <Users size={28} strokeWidth={1.7} />
                      </div>
                      <p>کاربری ثبت نشده است.</p>
                    </div>
                  </td>
                </tr>
              )}

              {users.map((user) => (
                <tr key={user.id}>
                  <td>
                    <div className="cell-user">
                      <span className="avatar avatar-sm" aria-hidden="true">
                        {(user.full_name || user.username).trim().charAt(0)}
                      </span>
                      <div>
                        <strong>{user.full_name || user.username}</strong>
                        <span className="ltr-text">{user.username}</span>
                      </div>
                      {user.id === currentUser?.id && <span className="badge badge-self">شما</span>}
                    </div>
                  </td>
                  <td>
                    <span className={`badge ${user.is_active ? "badge-ok" : "badge-off"}`}>
                      {user.is_active ? "فعال" : "غیرفعال"}
                    </span>
                  </td>
                  <td>
                    <div className="badge-row">
                      {permissionBadges(user).map((badge) => (
                        <span key={badge.key} className={`badge badge-${badge.tone}`}>
                          {badge.label}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="cell-apps">{applicationNames(user)}</td>
                  <td>
                    <div className="table-actions">
                      <button
                        type="button"
                        className="icon-btn"
                        title="ویرایش"
                        onClick={() => setDialog({ type: "edit", user })}
                      >
                        <Pencil size={17} strokeWidth={1.9} aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        className="icon-btn"
                        title="تغییر رمز عبور"
                        onClick={() => setDialog({ type: "password", user })}
                      >
                        <KeyRound size={17} strokeWidth={1.9} aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        className="icon-btn icon-btn-danger"
                        title="حذف"
                        disabled={user.id === currentUser?.id}
                        onClick={() => setDialog({ type: "delete", user })}
                      >
                        <Trash2 size={17} strokeWidth={1.9} aria-hidden="true" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {dialog?.type === "create" && (
        <UserFormModal
          applications={applications}
          busy={busy}
          error={dialogError}
          onClose={closeDialog}
          onSubmit={(payload) => run(() => createUser(payload))}
        />
      )}

      {dialog?.type === "edit" && (
        <UserFormModal
          user={dialog.user}
          applications={applications}
          busy={busy}
          error={dialogError}
          lockPermissions={dialog.user.id === currentUser?.id}
          onClose={closeDialog}
          onSubmit={(payload) => run(() => updateUser(dialog.user.id, payload))}
        />
      )}

      {dialog?.type === "password" && (
        <PasswordFormModal
          user={dialog.user}
          busy={busy}
          error={dialogError}
          onClose={closeDialog}
          onSubmit={(payload) => run(() => setUserPassword(dialog.user.id, payload))}
        />
      )}

      {dialog?.type === "delete" && (
        <ConfirmDialog
          title="حذف کاربر"
          message={`کاربر «${dialog.user.full_name || dialog.user.username}» و همه دسترسی‌هایش حذف می‌شود. این کار قابل بازگشت نیست.`}
          busy={busy}
          error={dialogError}
          onClose={closeDialog}
          onConfirm={() => run(() => deleteUser(dialog.user.id))}
        />
      )}
    </>
  );
}
